<template>
    <div class="welcome-more">
        <div class="dashboard-section">
            <div class="dashboard-card">
                <div class="card-header">
                    订单来源
                    <span class="data-count" v-if="totalCount > 0">(共 {{ totalCount }} 条数据)</span>
                    <a-spin v-if="loading" size="small" class="loading-spin" />
                    <!-- 关闭图标 -->
                    <CloseOutlined class="close-icon" @click="handleClose" />
                </div>

                <!-- 选项卡 -->
                <div class="tabs-container">
                    <a-tabs v-model:activeKey="activeTab" class="activity-tabs" @change="handleTabChange">
                        <a-tab-pane key="1" tab="月生成订单" />
                        <a-tab-pane key="2" tab="月预约订单" />
                        <a-tab-pane key="3" tab="月完结订单" />
                    </a-tabs>
                </div>

                <!-- 图表区域 - 确保容器存在 -->
                <div class="chart-scroll-container" ref="scrollContainer">
                    <div class="chart-wrapper">
                        <!-- 添加 v-if 确保容器渲染 -->
                        <div v-if="!hasData && !loading" class="no-data">
                            <a-empty description="暂无数据" />
                        </div>

                        <!-- 关键：使用 v-show 而不是 v-if，确保容器始终存在 -->
                        <div
                                ref="chartRef"
                                class="chart"
                                :style="{
                height: chartHeight + 'px',
                visibility: hasData ? 'visible' : 'hidden'
              }"
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, onMounted, onUnmounted, nextTick, computed, watch } from 'vue'
    import { useRouter } from 'vue-router'
    import { message, Tabs, Spin, Empty } from 'ant-design-vue'
    import { CloseOutlined } from '@ant-design/icons-vue'  // 导入关闭图标
    import * as echarts from 'echarts'
    import { dashboardAPI } from '@/services/dashboard'

    const { TabPane } = Tabs

    // 状态管理
    const activeTab = ref('1')
    const chartRef = ref(null)
    const chartInstance = ref(null)
    const loading = ref(false)
    const type = ref('total')
    const totalCount = ref(0)
    const chartHeight = ref(5000)
    const scrollContainer = ref(null)
    const isChartInitialized = ref(false)
    const isMounted = ref(false)

    const router = useRouter()

    // 计算属性
    const hasData = computed(() => totalCount.value > 0)

    // 关闭页面
    const handleClose = () => {
        // 方法1：返回上一页（推荐，如果是从首页点进来的）
        router.back()

        // 方法2：跳转到首页（如果需要）
        // router.push('/')

        // 方法3：如果是在新窗口打开的，可以关闭窗口
        // window.close()
    }


    // 监听路由参数变化
    watch(() => router.currentRoute.value.query, (query) => {
        if (query && query.type) {
            type.value = query.type
            if (isMounted.value) {
                reloadData()
            }
        }
    })

    // 初始化
    onMounted(async () => {
        isMounted.value = true

        // 从路由参数获取类型
        const query = router.currentRoute.value.query
        if (query && query.type) {
            type.value = query.type
        }

        console.log('组件已挂载，准备初始化图表')

        // 等待DOM渲染完成
        await nextTick()

        // 初始化图表
        initChart()

        // 加载数据
        if (isChartInitialized.value) {
            await loadChartData()
        }
    })

    // 初始化图表
    const initChart = () => {
        console.log('开始初始化图表，容器引用:', chartRef.value)

        if (!chartRef.value) {
            console.error('图表容器未找到，等待重试...')
            // 如果容器未找到，延迟重试
            setTimeout(() => {
                console.log('重试初始化图表...')
                initChart()
            }, 100)
            return
        }

        // 清理之前的实例
        if (chartInstance.value) {
            try {
                chartInstance.value.dispose()
                console.log('清理了之前的图表实例')
            } catch (e) {
                console.warn('清理图表实例时出错:', e)
            }
            chartInstance.value = null
        }

        try {
            // 创建新的图表实例
            chartInstance.value = echarts.init(chartRef.value)
            isChartInitialized.value = true

            console.log('图表实例创建成功:', chartInstance.value)

            // 设置默认的空配置
            const defaultOption = {
                title: {
                    text: '请稍候...',
                    left: 'center',
                    top: 'center',
                    textStyle: {
                        fontSize: 14,
                        color: '#999'
                    }
                },
                xAxis: {
                    show: false
                },
                yAxis: {
                    show: false
                },
                series: []
            }

            chartInstance.value.setOption(defaultOption)

            // 监听窗口大小变化
            window.addEventListener('resize', handleResize)

            console.log('图表初始化完成')

        } catch (error) {
            console.error('图表初始化失败:', error)
            isChartInitialized.value = false

            // 重试
            setTimeout(() => {
                console.log('图表初始化失败，正在重试...')
                initChart()
            }, 500)
        }
    }

    // 重新加载数据
    const reloadData = async () => {
        if (!isChartInitialized.value) {
            await initChart()
        }
        await loadChartData()
    }

    // 加载图表数据
    const loadChartData = async () => {
        if (!isChartInitialized.value) {
            console.warn('图表未初始化，先初始化图表')
            await initChart()

            if (!isChartInitialized.value) {
                console.error('图表初始化失败，无法加载数据')
                message.error('图表初始化失败')
                return
            }
        }

        loading.value = true

        try {
            const params = {
                date: parseInt(activeTab.value),
                type: type.value
            }

            console.log('正在请求数据，参数:', params)

            let response

            // 根据类型调用不同的接口
            switch (type.value) {
                case 'create':
                    response = await dashboardAPI.getCreateData(params)
                    break
                case 'appointment':
                    response = await dashboardAPI.getAppointmentData(params)
                    break
                case 'complete':
                    response = await dashboardAPI.getCompleteData(params)
                    break
                case 'total':
                default:
                    response = await dashboardAPI.getSourceData(params)
                    break
            }

            console.log('接口返回数据:', response)

            if (!response) {
                throw new Error('接口返回数据为空')
            }

            // 处理数据
            await processChartData(response)

        } catch (error) {
            console.error('加载数据失败:', error)
            message.error('数据加载失败: ' + (error.message || '未知错误'))

            // 显示空状态
            totalCount.value = 0
            if (chartInstance.value) {
                chartInstance.value.setOption({
                    title: {
                        text: '数据加载失败',
                        left: 'center',
                        top: 'center',
                        textStyle: {
                            fontSize: 14,
                            color: '#ff4d4f'
                        }
                    }
                })
            }
        } finally {
            loading.value = false
        }
    }

    // 处理图表数据
    const processChartData = async (data) => {
        if (!data) {
            totalCount.value = 0
            showNoData()
            return
        }

        let yAxisData = []
        let seriesData = []

        console.log('开始处理数据，数据结构:', Object.keys(data))

        // 优先使用 shopEvaluation_y 和 shopEvaluation_x
        if (data.shopEvaluation_y && data.shopEvaluation_x) {
            yAxisData = data.shopEvaluation_y
            seriesData = data.shopEvaluation_x
            console.log('使用 shopEvaluation_y/x 数据，长度:', yAxisData.length)

            // 其次使用 shop_evaluation
        } else if (data.shop_evaluation && Array.isArray(data.shop_evaluation)) {
            const shopEvaluation = data.shop_evaluation
            console.log('使用 shop_evaluation 数据，长度:', shopEvaluation.length)

            yAxisData = shopEvaluation.map((item, index) => {
                if (Array.isArray(item) && item.length > 0) {
                    return item[0] || `项目${index + 1}`
                }
                return item.title || `项目${index + 1}`
            })

            seriesData = shopEvaluation.map(item => {
                if (Array.isArray(item)) {
                    switch (activeTab.value) {
                        case '1': return item[4] || 0 // 月生成订单
                        case '2': return item[8] || 0 // 月预约订单
                        case '3': return item[12] || 0 // 月完结订单
                        default: return 0
                    }
                } else {
                    switch (activeTab.value) {
                        case '1': return item.month_create || 0
                        case '2': return item.month_appointment || 0
                        case '3': return item.month_complete || 0
                        default: return 0
                    }
                }
            })

        } else {
            console.warn('未识别的数据格式，keys:', Object.keys(data))
            showNoData()
            return
        }

        totalCount.value = yAxisData.length

        if (totalCount.value === 0) {
            console.warn('没有有效数据')
            showNoData()
            return
        }

        console.log('数据处理完成，准备更新图表')

        // 更新图表
        await updateChart(yAxisData, seriesData)
    }

    // 显示无数据状态
    const showNoData = () => {
        totalCount.value = 0
        if (chartInstance.value) {
            chartInstance.value.setOption({
                title: {
                    text: '暂无数据',
                    left: 'center',
                    top: 'center',
                    textStyle: {
                        fontSize: 14,
                        color: '#999'
                    }
                },
                xAxis: { show: false },
                yAxis: { show: false },
                series: []
            })
        }
    }

    // 更新图表
    const updateChart = async (yAxisData, seriesData) => {
        if (!chartInstance.value || !isChartInitialized.value) {
            console.error('图表实例不可用，无法更新')
            return
        }

        await nextTick()

        try {
            // 计算合适的图表高度
            const itemHeight = 35
            const padding = 150
            chartHeight.value = Math.max(500, yAxisData.length * itemHeight + padding)

            // 构建图表配置
            const option = {
                title: {
                    text: '测评活动 - ' + getTabTitle(activeTab.value),
                    left: 'center',
                    textStyle: {
                        fontSize: 16,
                        fontWeight: 'normal'
                    }
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    }
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '3%',
                    top: '50px',
                    containLabel: true
                },
                xAxis: {
                    type: 'value',
                    name: '订单数',
                    nameTextStyle: {
                        fontSize: 12
                    },
                    axisLabel: {
                        fontSize: 11
                    }
                },
                yAxis: {
                    type: 'category',
                    data: yAxisData,
                    axisLabel: {
                        fontSize: 11,
                        interval: 0
                    },
                    axisTick: {
                        alignWithLabel: true
                    }
                },
                series: [
                    {
                        name: '订单数',
                        type: 'bar',
                        data: seriesData,
                        barWidth: '60%',
                        itemStyle: {
                            color: '#4BCA7A',
                            borderRadius: [0, 4, 4, 0]
                        },
                        label: {
                            show: true,
                            position: 'right',
                            fontSize: 11,
                            formatter: '{c}'
                        }
                    }
                ]
            }

            console.log('正在更新图表配置...')
            chartInstance.value.setOption(option, true)
            console.log('图表更新完成')

            // 延迟调整大小
            setTimeout(() => {
                if (chartInstance.value) {
                    console.log('调整图表大小...')
                    chartInstance.value.resize()
                }
            }, 100)

        } catch (error) {
            console.error('更新图表失败:', error)
            throw error
        }
    }

    // 获取选项卡标题
    const getTabTitle = (key) => {
        switch (key) {
            case '1': return '月生成订单'
            case '2': return '月预约订单'
            case '3': return '月完结订单'
            default: return '订单统计'
        }
    }

    // 处理选项卡变化
    const handleTabChange = () => {
        console.log('选项卡变化:', activeTab.value)
        loadChartData()
    }

    // 处理窗口大小变化
    const handleResize = () => {
        if (chartInstance.value && isChartInitialized.value) {
            try {
                // 防抖处理
                clearTimeout(window.resizeTimer)
                window.resizeTimer = setTimeout(() => {
                    chartInstance.value.resize()
                }, 200)
            } catch (error) {
                console.warn('调整图表大小失败:', error)
            }
        }
    }

    // 清理
    onUnmounted(() => {
        console.log('组件销毁，清理资源...')
        isMounted.value = false

        // 移除事件监听
        window.removeEventListener('resize', handleResize)

        // 清理图表实例
        if (chartInstance.value) {
            try {
                chartInstance.value.dispose()
                console.log('图表实例已清理')
            } catch (e) {
                console.warn('清理图表实例时出错:', e)
            }
            chartInstance.value = null
        }

        isChartInitialized.value = false
    })
</script>

<style scoped>
    .welcome-more {
        padding: 16px;
        background: #f0f2f5;
        min-height: 100vh;
        height: 100vh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }

    .dashboard-section {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    .dashboard-card {
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 1px 2px -2px rgba(0, 0, 0, 0.16),
        0 3px 6px 0 rgba(0, 0, 0, 0.12),
        0 5px 12px 4px rgba(0, 0, 0, 0.09);
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
    }

    .card-header {
        padding: 16px 24px;
        font-size: 16px;
        font-weight: 500;
        border-bottom: 1px solid #f0f0f0;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .card-header .data-count {
        font-size: 14px;
        color: #666;
        font-weight: normal;
    }

    .loading-spin {
        margin-left: 8px;
    }

    .tabs-container {
        padding: 0 24px;
        border-bottom: 1px solid #f0f0f0;
        flex-shrink: 0;
    }

    .activity-tabs :deep(.ant-tabs-nav) {
        margin: 0;
    }

    .activity-tabs :deep(.ant-tabs-tab) {
        padding: 12px 0;
        margin-right: 32px;
    }

    .activity-tabs :deep(.ant-tabs-tab-active) {
        font-weight: 500;
    }

    .activity-tabs :deep(.ant-tabs-tab-active .ant-tabs-tab-btn) {
        color: #1890ff;
    }

    /* 图表区域 */
    .chart-scroll-container {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
        min-height: 0;
    }

    .chart-wrapper {
        position: relative;
        min-height: 500px;
        width: 100%;
    }

    .chart {
        width: 100%;
        min-height: 500px;
        background: #fff;
    }

    .no-data {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 300px;
        width: 100%;
    }

    /* 响应式设计 */
    @media (max-width: 768px) {
        .welcome-more {
            padding: 8px;
        }

        .dashboard-card .card-header {
            padding: 12px 16px;
        }

        .tabs-container {
            padding: 0 16px;
        }

        .activity-tabs :deep(.ant-tabs-tab) {
            margin-right: 16px;
        }

        .chart-scroll-container {
            padding: 10px;
        }
    }
</style>